(() => {
  'use strict';
  if (customElements.get('apex-halloween-pig')) return;
  const EYE_CROP = { x: 155, y: 160, w: 338, h: 168 };
  const EYES = [
    {
      path: 'M183 163 C197 181 216 160 236 177 C258 194 258 230 246 246 C228 253 218 266 211 273 C194 280 181 261 161 258 L155 251 C164 245 170 247 174 247 C166 225 178 193 194 179 Z',
      box: { x: 155, y: 160, w: 110, h: 124 }, closeY: 237,
      line: 'M178 238 Q208 258 247 226', color: '#fc8b97'
    },
    {
      path: 'M355 289 C355 252 383 218 411 207 C433 200 450 212 460 220 C470 230 479 230 487 226 C483 236 474 240 471 240 C483 263 471 296 455 309 L467 322 C443 311 424 313 405 313 C380 317 354 313 355 289 Z',
      box: { x: 350, y: 200, w: 143, h: 128 }, closeY: 275,
      line: 'M364 278 Q410 307 468 269', color: '#fc8d98'
    }
  ];
  class HalloweenPig extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.paused = document.body.hasAttribute('data-halloween-paused');
      this.canvas = this.querySelector('.halloween-pig__eyes');
      this.context = this.canvas?.getContext('2d');
      this.poster = this.querySelector('.halloween-pig__body');
      this.eyesReady = false; this.blinkCount = 0; this.minOpen = 1;
      this.querySelector('[data-pig-greet]')?.addEventListener('click', () => this.greet(), { signal });
      document.querySelector('[data-halloween-pumpkin]')?.addEventListener('click', () => {
        this.greet();
        const devil = document.querySelector('apex-devil');
        if (devil?.ready && !devil.paused && !devil.reduced.matches && devil.dataset.motion !== 'false') {
          devil.blinkStart = devil.clock; devil.nextBlink = devil.clock + 4000; devil.render();
        }
      }, { signal });
      this.reduced.addEventListener('change', () => this.sync(), { signal });
      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.sync(); }, { threshold: .1 });
      this.observer.observe(this);
      this.prepareEyes();
      this.sync();
    }
    async prepareEyes() {
      if (!this.context || !this.poster) return;
      try {
        await this.poster.decode();
        if (!this.isConnected || this.controller.signal.aborted) return;
        this.eyes = EYES.map(eye => {
          const shape = new Path2D(eye.path), source = document.createElement('canvas');
          source.width = eye.box.w; source.height = eye.box.h;
          const c = source.getContext('2d');
          c.translate(-eye.box.x, -eye.box.y); c.clip(shape); c.drawImage(this.poster, 0, 0);
          return { ...eye, shape, lid: new Path2D(eye.line), source };
        });
        this.eyesReady = true; this.setAttribute('data-eyes-ready', ''); this.sync();
      } catch { this.setAttribute('data-eyes-fallback', ''); }
    }
    disconnectedCallback() {
      this.controller?.abort(); this.observer?.disconnect();
      clearTimeout(this.greetingTimer); clearTimeout(this.partyTimer);
      clearTimeout(this.blinkTimer); this.blinkTimer = null; this.stopBlink();
      this.removeAttribute('data-playing');
    }
    canPlay() { return this.isConnected && this.visible && !this.paused && !this.reduced.matches && this.dataset.motion !== 'false' && !document.hidden; }
    sync() {
      this.toggleAttribute('data-playing', this.canPlay());
      if (!this.canPlay()) this.removeAttribute('data-party');
      if (this.canPlay() && this.eyesReady) this.scheduleBlink();
      else { clearTimeout(this.blinkTimer); this.blinkTimer = null; this.stopBlink(); }
    }
    scheduleBlink() {
      if (this.blinkTimer || this.blinkFrame || !this.canPlay() || !this.eyesReady) return;
      this.blinkTimer = setTimeout(() => { this.blinkTimer = null; this.startBlink(); }, 3800 + Math.random() * 4200);
    }
    stopBlink() {
      cancelAnimationFrame(this.blinkFrame); this.blinkFrame = null;
      this.context?.clearRect(0, 0, EYE_CROP.w, EYE_CROP.h);
      this.removeAttribute('data-blinking');
    }
    startBlink() {
      if (!this.canPlay() || !this.eyesReady) return;
      clearTimeout(this.blinkTimer); this.blinkTimer = null; this.stopBlink();
      const start = performance.now(); this.blinkCount++;
      this.setAttribute('data-blinking', '');
      const frame = now => {
        if (!this.canPlay()) { this.stopBlink(); return; }
        const progress = Math.min(1, (now - start) / 210);
        const open = 1 - Math.sin(progress * Math.PI);
        this.minOpen = Math.min(this.minOpen, open); this.renderEyes(open);
        if (progress < 1) this.blinkFrame = requestAnimationFrame(frame);
        else { this.stopBlink(); this.scheduleBlink(); }
      };
      this.blinkFrame = requestAnimationFrame(frame);
    }
    renderEyes(open) {
      const c = this.context;
      if (!c || !this.eyesReady) return;
      c.clearRect(0, 0, EYE_CROP.w, EYE_CROP.h);
      if (open >= .999) return;
      c.save(); c.translate(-EYE_CROP.x, -EYE_CROP.y);
      for (const eye of this.eyes) {
        c.save(); c.clip(eye.shape);
        c.fillStyle = eye.color; c.fillRect(eye.box.x, eye.box.y, eye.box.w, eye.box.h);
        const y = eye.closeY + (eye.box.y - eye.closeY) * open;
        if (open > .025) c.drawImage(eye.source, eye.box.x, y, eye.box.w, eye.box.h * open);
        if (open < .15) {
          c.globalAlpha = Math.max(0, 1 - open / .15);
          c.strokeStyle = '#080808'; c.lineWidth = 5; c.lineCap = 'round'; c.stroke(eye.lid);
        }
        c.restore();
      }
      c.restore();
    }
    setPaused(value) {
      this.paused = value;
      document.body.toggleAttribute('data-halloween-paused', value);
      this.sync();
    }
    greet() {
      clearTimeout(this.greetingTimer); clearTimeout(this.partyTimer);
      this.setAttribute('data-greeting', '');
      const status = document.querySelector('[data-halloween-status]');
      if (status) status.textContent = 'Boo! Happy Halloween from the little vampire pig.';
      if (this.canPlay()) {
        this.startBlink();
        this.removeAttribute('data-party'); void this.offsetWidth;
        this.setAttribute('data-party', '');
        this.partyTimer = setTimeout(() => this.removeAttribute('data-party'), 1000);
      }
      this.greetingTimer = setTimeout(() => { this.removeAttribute('data-greeting'); if (status) status.textContent = ''; }, 2200);
    }
  }
  customElements.define('apex-halloween-pig', HalloweenPig);
})();
